import { AppLocale } from "@/i18n/config";
import { ProjectCollaborator, SubscriptionInvoiceStatus, SubscriptionStatus, SubscriptionTier } from "@prisma/client";
import { clsx, type ClassValue } from "clsx";
import { CookieValueTypes, OptionsType, getCookie, setCookie } from "cookies-next";
import { formatDate, formatRelative, getHours, getMinutes, isSameDay } from "date-fns";
import { enUS, ptBR } from 'date-fns/locale';
import MarkdownIt from 'markdown-it';
import { getTranslations } from "next-intl/server";
import { NextRequest, NextResponse } from "next/server";
import { twMerge } from "tailwind-merge";
import { PermissionInfo } from "./models/permissionInfo";
import { Plan, RecurringType } from "./models/plan";
import { PLANS } from "./plansFeatures";
import { ActiveUser } from "./services/accountService";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const md = new MarkdownIt()

type GroupedItems<T> = { [key: string]: T[] };

export const COOKIE_SIDEBAR_STATE = "kl_sidebar_state";
export const COOKIE_VISTOR_ID = "kl_vid";
export const COOKIE_PLAN_INTERESTED = "kl_plan_id";
export const LOCALSTORAGE_CURRENT_WORKSPACE_ID = "kr_curr_wsid";
export const DEFAULT_INTERVAL_HOUR = 30;
export const DEFAULT_ALLOW_WEEK_DAYS = "0111110";
export const DEFAULT_ALLOW_HOURS_DAYS = "08-18|08-18|08-18|08-18|08-18|08-18|08-18";
export const REGEX_PHONENUMBER = /^\+?[1-9]\d{1,14}$/
export const REGEX_HOUR = /^\d{2}:\d{2}$/
export const NO_PICTURE = `${process.env.NEXT_PUBLIC_STORAGE_URL}/public/logo/logo_k.png`;
export const MAX_FILE_SIZE = 1 * 1024 * 1024; // 5MB
export const ACCEPTED_FILE_TYPES = ["image/jpeg", "image/png", "application/pdf"];

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export const Utils = {
  color: {
    getRandomHexColor() {
      // Generate a random number between 0 and 16777215 (which is #FFFFFF in hex)
      const randomNumber = Math.floor(Math.random() * 16777215);

      // Convert the number to a hex string and pad with zeros if necessary
      const hexColor = `#${randomNumber.toString(16).padStart(6, '0')}`;

      return hexColor;
    },

    getSolidColors() {
      return ["#CD5C5C", "#F08080", "#FA8072", "#E9967A", "#FFA07A", "#DC143C", "#FF0000", "#8B0000", "#FFC0CB", "#FFB6C1", "#FF69B4", "#FF1493", "#C71585", "#DB7093", "#FF7F50", "#FF6347", "#FF4500", "#FF8C00", "#FFA500", "#FFD700", "#FFFF00", "#FFFFE0", "#FFFACD", "#FAFAD2", "#FFEFD5", "#FFE4B5", "#FFDAB9", "#EEE8AA", "#F0E68C", "#BDB76B", "#E6E6FA", "#D8BFD8", "#DDA0DD", "#EE82EE", "#DA70D6", "#FF00FF", "#BA55D3", "#9370DB", "#663399", "#8A2BE2", "#9400D3", "#9932CC", "#8B008B", "#800080", "#4B0082", "#6A5ACD", "#483D8B", "#7B68EE", "#ADFF2F", "#7FFF00", "#7CFC00", "#00FF00", "#32CD32", "#98FB98", "#90EE90", "#00FA9A", "#00FF7F", "#3CB371", "#2E8B57", "#228B22", "#008000", "#006400", "#9ACD32", "#6B8E23", "#556B2F", "#66CDAA", "#8FBC8B", "#20B2AA", "#008B8B", "#008080", "#00FFFF", "#E0FFFF", "#AFEEEE", "#7FFFD4", "#40E0D0", "#48D1CC", "#00CED1", "#5F9EA0", "#4682B4", "#B0C4DE", "#B0E0E6", "#ADD8E6", "#87CEEB", "#87CEFA", "#00BFFF", "#1E90FF", "#6495ED", "#4169E1", "#0000FF", "#0000CD", "#00008B", "#191970", "#FFF8DC", "#FFEBCD", "#FFE4C4", "#FFDEAD", "#F5DEB3", "#DEB887", "#D2B48C", "#BC8F8F", "#F4A460", "#DAA520", "#B8860B", "#CD853F", "#D2691E", "#8B4513", "#A0522D", "#A52A2A", "#800000", "#FFFFFF", "#FFFAFA", "#F0FFF0", "#F5FFFA", "#F0FFFF", "#F0F8FF", "#F8F8FF", "#F5F5F5", "#FFF5EE", "#F5F5DC", "#FDF5E6", "#FFFFF0", "#FAEBD7", "#FAF0E6", "#FFF0F5", "#FFE4E1", "#DCDCDC", "#D3D3D3", "#C0C0C0", "#A9A9A9", "#808080", "#696969", "#778899", "#708090", "#2F4F4F", "#000000"]
    },

    getColorBySubscriptionStatus(status: SubscriptionStatus): string {
      switch (status) {
        case 'paused': return "hsl(32.1 94.6% 43.7%)";
        case 'active': return "hsl(161.4 93.5% 30.4%)";
        case 'canceled': return "hsl(346.8 77.2% 49.8%)";
      }
      return "hsl(215.3 19.3% 34.5%)"
    },

    getColorByInvoiceStatus(status: SubscriptionInvoiceStatus): string {
      switch (status) {
        case 'open': return 'hsl(191.6 91.4% 36.5%)';
        case 'paid': return "hsl(161.4 93.5% 30.4%)";
        case 'uncollectible': return "hsl(32.1 94.6% 43.7%)";
        case 'void': return "hsl(346.8 77.2% 49.8%)";
      }

      return "hsl(215.3 19.3% 34.5%)";
    },
  },
  string: {
    slugify(text: string) {
      return text
        .toString()                 // Converte para string
        .normalize('NFD')           // Remove acentos
        .replace(/[\u0300-\u036f]/g, '') // Remove caracteres especiais
        .toLowerCase()              // Converte para minúsculas
        .trim()                     // Remove espaços em branco
        .replace(/\s+/g, '-')       // Substitui espaços por hifens
        .replace(/[^\w\-]+/g, '')   // Remove caracteres inválidos
        .replace(/\-\-+/g, '-');    // Remove hifens duplicados
    },
    removeAccents(str: string): string {
      if (!str) return '';

      return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9 ]/g, "");
    },
    normalizeString(str: string): string {
      if (!str) return '';

      return Utils.string.removeAccents(str).toLowerCase(); // Makes it case insensitive
    },
    copyToClipboard(text: string): Promise<void> {
      try {
        return navigator.clipboard.writeText(text);
      } catch (error: unknown) {
        console.error('Erro in copyToClipboard: ', error);
        return Promise.resolve();
      }
    },
    stripHTML(text: string) {
      if (!text) return '';
      return text.replace(/<\/?[^>]+(>|$)/g, "");
    },
    generateRandomString(length: number) {
      return Math.random().toString(36).substring(2, 2 + length);
    },
    isHTML(text: string) {
      const htmlRegex = /<\/?[a-z][\s\S]*>/i
      return htmlRegex.test(text.trim())
    },
    isMarkdown(content: string): boolean {
      if (!content || typeof content !== 'string') return false

      const rendered = md.render(content).trim()
      const isProbablyHtml = /^<.*?>[\s\S]*<\/.*?>$/.test(content.trim())

      // Se a string de entrada for bem diferente do HTML gerado, assume-se Markdown
      return !isProbablyHtml && rendered !== content.trim()
    },
    detectContentType(content: string): 'html' | 'markdown' | 'unknown' {
      if (Utils.string.isHTML(content)) return 'html'
      if (Utils.string.isMarkdown(content)) return 'markdown'
      return 'unknown'
    }
  },
  http: {
    async fetchData<T>(url: string, method: HttpMethod = 'GET', data?: unknown): Promise<T> {
      const language = getCookie('NEXT_LOCALE') ?? 'en';

      const options: RequestInit = {
        method,
        credentials: 'include',
        headers: {
          'Accept-Language': language as string,
          'Content-Type': 'application/json',
        },
      };

      if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        return Promise.reject(await response.json());
      }

      return response.json() as Promise<T>;
    },
    async postFormData<T>(url: string, data: FormData): Promise<T> {
      const language = getCookie('NEXT_LOCALE') ?? 'en';

      const options: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept-Language': language as string
        },
      };

      options.body = data;

      const response = await fetch(url, options);
      if (!response.ok) {
        return Promise.reject(await response.json());
      }

      return response.json() as Promise<T>;
    },
    async extractSubdomain(host: string): Promise<string | null> {
      // Ignora hosts vazios
      if (!host) return null;

      const hostWithoutPort = host.split(':')[0];

      const subdomain = hostWithoutPort
        .replace('www.', '')
        .replace('localhost:3000', '')
        .replace('kiwilog.app', '')
        .split('.')[0];

      if (subdomain === 'localhost') return null;

      return subdomain;
    },
  },
  httpResponses: {
    unauthorized() {
      return NextResponse.json(
        { error: 'Não autorizado' },
        { status: 401 }
      )
    },
    notFound() {
      return NextResponse.json(
        { error: 'Não encontrado' },
        { status: 404 }
      )
    },
    badRequest(message: string = 'Bad request') {
      return NextResponse.json(
        { error: message },
        { status: 400 }
      )
    },
    internalServerError() {
      return NextResponse.json(
        { error: 'Erro interno do servidor' },
        { status: 500 }
      )
    },
    ok() {
      return NextResponse.json(
        {},
        { status: 200 }
      )
    },
    created() {
      return NextResponse.json(
        {},
        { status: 201 }
      )
    }
  },
  array: {
    sort<T>(array: T[], property: keyof T, order: 'asc' | 'desc' = 'asc'): T[] {
      return array.sort((a, b) => {
        const valueA = a[property];
        const valueB = b[property];

        if (valueA < valueB) {
          return order === 'asc' ? -1 : 1;
        }
        if (valueA > valueB) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      });
    },
    groupBy<T>(array: T[], key: keyof T): GroupedItems<T> {
      return array.reduce((result, currentItem) => {
        const groupKey = currentItem[key] as string;
        if (!result[groupKey]) {
          result[groupKey] = [];
        }
        result[groupKey].push(currentItem);
        return result;
      }, {} as GroupedItems<T>);
    },
    sum(array: number[]) {
      return array.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
    }
  },
  date: {
    getLocale(appLocale: AppLocale) {
      switch (appLocale as AppLocale) {
        case 'pt': return ptBR;
        // case 'es': return es;
      }

      return enUS;
    },
    getUTCDate(date: Date): Date {
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth(); // Months are zero-based
      const day = date.getUTCDate();
      const hours = date.getUTCHours();
      const minutes = date.getUTCMinutes();
      const seconds = date.getUTCSeconds();

      return new Date(year, month, day, hours, minutes, seconds)
    },
    format(date: Date, format: string, appLocale?: AppLocale): string {

      if (!date) {
        return '';
      };

      const locale = Utils.date.getLocale(appLocale as AppLocale);

      return formatDate(date, format, {
        locale
      })
    },
    formatRange(start: Date, end: Date, format: string, appLocale: AppLocale): string {

      const sameDay = isSameDay(start, end);

      if (!sameDay) {
        return `${Utils.date.format(start, format, appLocale)} — ${Utils.date.format(end, format, appLocale)}`;
      } else {
        return Utils.date.format(start, format, appLocale);
      }
    },
    formatRangeBySettings(start: Date, end: Date, appLocale: AppLocale): string {
      const format = appLocale === 'pt' ? 'dd/MM/yyyy HH:mm' : 'MM/dd/yyyy HH:mm';
      return this.formatRange(start, end, format, appLocale);
    },
    formatTime(date: Date) {
      const hour = getHours(date);
      const minute = getMinutes(date);


      if (minute === 0) {
        return `${hour}h`
      }

      return `${hour}h${minute}m`
    },
    formatToICSDate(date: Date) {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    },
    formatRelative(date: Date, appLocale: AppLocale) {
      const locale = Utils.date.getLocale(appLocale as AppLocale);
      return formatRelative(date, new Date(), { locale });
    }
  },
  mappers: {

  },
  validation: {
    isImage: (file: File): boolean => {
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      return validImageTypes.includes(file.type);
    },
    isSlug: (slug: string): boolean => {

      if (!slug || slug.length < 3) return false;

      const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
      return slugRegex.test(slug);
    }
  },
  localStorage: {
    get(key: string): string | null {
      return window.localStorage.getItem(key)
    },
    set(key: string, value: string) {
      window.localStorage.setItem(key, value);
    }
  },
  cookie: {
    async get(key: string): Promise<CookieValueTypes> {
      return getCookie(key);
    },
    set(key: string, value: string, options?: OptionsType) {
      const defaultOptions: OptionsType = {
        domain: '.kiwilog.app',
        maxAge: 60 * 60 * 24 * 365, // 1 ano
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'none',
        ...options // Permite sobrescrever as opções padrão
      };
      setCookie(key, value, defaultOptions);
    },
    setForWidget(key: string, value: string, options?: OptionsType) {
      const widgetOptions: OptionsType = {
        domain: '.kiwilog.app',
        maxAge: 60 * 60 * 24 * 365, // 1 ano
        secure: true, // Obrigatório quando sameSite=none
        sameSite: 'none', // Permite cookies de terceiros
        ...options
      };
      setCookie(key, value, widgetOptions);
    },
    setSmart(key: string, value: string, options?: OptionsType) {
      // Detecta automaticamente se está em iframe e usa a configuração apropriada
      if (Utils.iframe.isInIframe()) {
        this.setForWidget(key, value, options);
      } else {
        this.set(key, value, options);
      }
    }
  },
  iframe: {
    isInIframe(): boolean {
      return typeof window !== 'undefined' && window.self !== window.top;
    }
  },
  intl: {
    async getTranslations(request: NextRequest, namespace: string) {
      const locale = request.headers.get('accept-language')?.split(',')[0] || 'en';
      return await getTranslations({ locale, namespace });
    },
    getCountryCodeFromLocale(locale: AppLocale): string {
      switch (locale) {
        case 'pt': return 'BR';
        // case 'es': return 'ES';
        default: return 'US';
      }
    }
  },
  events: {
    disableOutsideClick(e: Event) {
      e.preventDefault();
    }
  },
  subscription: {
    getPlanByTier(tier: SubscriptionTier): Plan {
      switch (tier) {
        case 'starter':
          return PLANS.starter;
        case 'professional':
          return PLANS.pro;
      }

      return PLANS.free;
    },
    // hasAccess: (subscription: Subscription, feature: keyof (Plan)): boolean => {

    //   if (subscription === null) return false;
    //   return PLANS[subscription.tier][feature];
    // }
  },
  parse: {
    decimalToNumber: (value: string): number => {
      return parseFloat(value.toString());
    }
  },
  permissions: {
    canChangeRole(permissionInfo: PermissionInfo | null, collaborator: ProjectCollaborator | null): boolean {

      if (!permissionInfo || !collaborator) return false;

      //Não é possível alterar o papel de si mesmo
      if (permissionInfo.userId === collaborator.userId) return false;

      //Somente admin e donos do projeto podem alterar o papel de um colaborador
      if (permissionInfo.role === "admin" || permissionInfo.role === "owner") return true;

      return false;
    },
    canRemoveCollaborator(permissionInfo: PermissionInfo | null, collaborator: ProjectCollaborator | null, activeUser: ActiveUser | null): boolean {
      if (!permissionInfo || !collaborator || !activeUser) return false;

      //Usuário não pode remover a si mesmo
      if (activeUser.id === collaborator.userId) return false;

      //Somente donos do projeto podem remover um colaborador
      if (permissionInfo.role === "owner") return true;

      return false;
    },
    canInviteCollaborator(permissionInfo: PermissionInfo | null): boolean {
      if (!permissionInfo) return false;

      return permissionInfo.canInviteCollaborator;
    }
  },
  tier: {
    getKey(tierCode: string, recurring: RecurringType, currencyCode: string) {
      return `${tierCode}_${recurring}_${currencyCode}`.toLocaleLowerCase()
    },
    // getTiersWithPrices(stripePrices: Stripe.Price[], currencyCode: string, recurring: RecurringType, subscription: Subscription): TierPrice[] {
    //   const result: TierPrice[] = [];

    //   if (stripePrices === null) {
    //     stripePrices = [];
    //   }

    //   let lookup_key = "";
    //   PLANS.forEach((feature) => {
    //     tier.currency = currencyCode;
    //     tier.recurring = recurring;
    //     tier.selected = tier.code === subscription?.tier

    //     if (tier.code === 'free') {
    //       result.push({ ...tier, currency: currencyCode });
    //     } else {
    //       lookup_key = Utils.tier.getKey(tier.code, recurring, currencyCode);

    //       const price = stripePrices.find(i => i.lookup_key === lookup_key);
    //       if (!!price) {
    //         tier.stripePriceId = price.id;
    //         tier.stripePriceKey = price.lookup_key;
    //         tier.price = price.unit_amount / 100
    //       } else {
    //         tier.price = null;
    //       }
    //       result.push(tier);
    //     }
    //   });

    //   return result;
    // },
    getTierInfoByKey(key: string): { code: SubscriptionTier, recurring: RecurringType } {

      if (!key) return {
        code: 'free',
        recurring: 'month'
      };

      const splittedData = key.split("_");

      let data: { code: SubscriptionTier, recurring: RecurringType } = {
        code: 'free',
        recurring: 'month'
      };

      if (splittedData.length === 3) {
        data = {
          code: splittedData[0] as SubscriptionTier,
          recurring: splittedData[1] as RecurringType
        };
      }

      return data;
    }
  },
  // tiptap: {
  //   parseContentToTiptapJSON(content: string): JSONContent {
  //     const isHtml = /<\/?[a-z][\s\S]*>/i.test(content.trim())

  //     const html = isHtml ? content : md.render(content)

  //     const editor = new Editor({
  //       extensions: [StarterKit],
  //       content: html,
  //     })

  //     return editor.getJSON()
  //   }
  // },
  image: {
    getExtensionFromMimeType(mimeType: string): string {
      const mimeToExtension: Record<string, string> = {
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/svg+xml': '.svg',
        'image/webp': '.webp',
        'image/tiff': '.tiff',
        'image/bmp': '.bmp',
        'application/pdf': '.pdf',
        // Add more MIME types as needed
      };

      return mimeToExtension[mimeType] || '';
    },
    getUserImageUrl(image: string | null): string | null {
      if (!image) return null;
      debugger;
      if (image.startsWith('/users')) {
        return `${process.env.NEXT_PUBLIC_STORAGE_URL}${image}`;
      }
      return image;
    },
    getImageUrl(imagePath: string | null): string {
      if (!imagePath) return '';

      let storageUrl = process.env.NEXT_PUBLIC_STORAGE_URL as string;

      if (storageUrl.endsWith('/')) {
        storageUrl = storageUrl.slice(0, -1);
      }

      if (imagePath.startsWith('/')) {
        return `${storageUrl}${imagePath}`;
      }

      return `${storageUrl}/${imagePath}`;
    }
  },
  project: {
    getPublicUrl(subdomain: string): string {
      if (process.env.NODE_ENV === 'development') {
        return `http://${subdomain}.localhost:3000`;
      }
      return `https://${subdomain}.kiwilog.app`;
    },
    getNewsHubPublicUrl(subdomain: string): string {
      const projectUrl = this.getPublicUrl(subdomain);
      return `${projectUrl}/news`;
    },
    getWidgetPublicUrl(subdomain: string, widgetId: string): string {
      const projectUrl = this.getPublicUrl(subdomain);
      return `${projectUrl}/wgt/${widgetId}`;
    }
  },
  widget: {
    generateWidgetId(): string {
      return `KW-${Utils.string.generateRandomString(12).toUpperCase()}`;
    },
    generateScript(subdomain: string, widgetId: string): string {

      const appUrl = process.env.NEXT_PUBLIC_APP_URL;
      const domain = appUrl?.replace('https://', '').replace('http://', '');

      return `
(function(w,d,s,o,f,js,fjs){
      w['kiwilog']=o;w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
      js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
      js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
}(window,document,'script','kw','${appUrl}/widget/launcher.js'));

setTimeout(() => {
      window.kw('init', {
      appId: '${widgetId}',
      position: 'bottom-right',
      widgetDomain: '${!!subdomain ? `${subdomain}.${domain}` : domain}'
      });
}, 1000);`;
    }
  }
}

